import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import {
  CampaignBannerMapState,
  CreativeOfCampaign,
  isArchivedCampaignCreative
} from 'core/creative/Creative';
import _ from 'lodash';
import { toast } from 'react-toastify';
import i18n from 'i18next';
import {
  DefaultCreativeListModel,
  CreativeListColumns,
  CreativeListModel
} from 'components/CampaignCreativeList/CreativeListModel';
import { L1Object } from 'core/l1Object/L1Object';
import rtbBindingListFormatters from 'components/CampaignCreativeList/defaultListFormatters';
import { CreativeManagementStateContext } from './CreativeManagementStateContext';
import {
  BindingManager,
  DefaultBindingManager
} from 'core/binding/BindingManager';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { Permission } from 'core/auth/Permission';
import {
  addOnEnabled,
  notArchivedChannel,
  hasFuncs,
  isPmax3Order,
  notSelfServeAdObject
} from 'core/permission/PermissionDSL';
import { PermissionItem } from 'core';
import { DefaultRtbCampaignManager } from 'core/rtbCampaign/RtbCampaignManager';
import { CreativeBindData } from 'core/rtbCampaign/CreativeBindData';
import { AdType, isOneForAllAdType } from 'core/rtbCampaign/RtbCampaign';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { ChannelType } from 'core/channelGroup/ChannelGroup';
import {
  isEpaperChannelGroup,
  isEpaperSpaceQuotaFull
} from 'core/channelGroup/EpaperChannelGroup';

export interface CreativeOverviewStateContentModel {
  readonly state: CreativeOverviewStateContentState;
  readonly channel: string;
  readonly event: UpdateEventListener<CreativeOverviewStateContentModel>;
  readonly creatives: CreativeOfCampaign[];
  readonly selectedCreatives: number[];
  readonly stateContext?: CreativeManagementStateContext;
  readonly campaignInfo: CreativeBindData;
  readonly closingReportEnabled: boolean;
  readonly showCreateCreativeBtn: boolean;
  readonly columnsToShow?: string[];
  readonly l1Object?: L1Object;
  readonly bindPermissionAware: PermissionItem;
  readonly onlyOneActiveCreative: boolean;
  l3ObjectStateFn: () => void;
  goCreateCreativeFn: () => void;
  handleOnSelect(creativeId: number): void;
  handleOnSelectAll(): void;
  handleOnUnSelectAll(): void;
  activeCreatives(): Promise<void>;
  deactiveCreatives(): Promise<void>;
  setStateContext(stateContext: CreativeManagementStateContext): void;
  getEditPath(creativeId: number): string;
  /**
   * 這個活動是否為只能唯讀的 One For All 活動：不能綁定、新增或啟用素材，只能停用。
   */
  readonly isReadOnlyCampaign: boolean;
  /**
   * 能不能再讓一份素材進到這個活動。
   *
   * 同時決定「綁定素材」和「新增素材」兩顆按鈕——新增出來的素材一樣會綁進來而且是
   * 啟用的，兩條路要一起擋才有意義。
   */
  canBind(): boolean;
  canActive(): boolean;
  canDeactive(): boolean;
  /**
   * canBind() 為 false 時要顯示的理由。
   *
   * 理由收在 model 而不是留給畫面各自判斷：CampaignListModel.getActivateDisabledHint 已經
   * 為同一件事立下 pattern，那邊的理由是「愈長愈多，畫面各自判斷一次就會出現『補了預算才
   * 發現還有另一個理由』這種來回」。名額讓這裡也變成多重理由了，而且優先順序留在畫面上
   * 沒辦法不 render 就測。
   *
   * @param target 被擋住的是「綁定素材」還是「新增素材」——兩顆鍵的預設文案不同
   */
  getBindBlockedTip(target: 'bind' | 'create'): string | undefined;
  /** canActive() 為 false 時要顯示的理由。優先順序與 getBindBlockedTip 一致。 */
  getActivateBlockedTip(): string | undefined;
  getCreativeListModel(): CreativeListModel;
}

export type CreativeOverviewStateContentProps = {
  readonly model: CreativeOverviewStateContentModel;
};

export type CreativeOverviewStateContentState = {
  readonly selectedCreatives: number[];
  readonly loading: boolean;
  readonly dirty: boolean;
};

export class DefaultCreativeOverviewStateContentModel implements CreativeOverviewStateContentModel {
  event: FireableUpdateEventListener<CreativeOverviewStateContentModel>;
  selectedCreatives: number[];
  loading: boolean;
  stateContext?: CreativeManagementStateContext;
  dirty: boolean;
  showCreateCreativeBtn = true;
  isDraft = false;
  bindingIdMap: Record<number, string> = {};
  channelGroupSetting?: ChannelGroupSetting;

  constructor(
    public l1Object: L1Object,
    public campaignInfo: CreativeBindData,
    public creatives: CreativeOfCampaign[],
    public closingReportEnabled: boolean,
    protected orderNumber: string,
    public l3ObjectStateFn,
    public goCreateCreativeFn,
    protected refreshCreative,
    protected bindingManager: BindingManager = new DefaultBindingManager(),
    protected rtbCampaignManager = new DefaultRtbCampaignManager()
  ) {
    this.event =
      new FireableUpdateEventListener<CreativeOverviewStateContentModel>();
    this.selectedCreatives = [];
    this.loading = false;
    this.dirty = false;
    this.isDraft = !!campaignInfo.draftId;
    creatives.forEach((creative) => {
      this.bindingIdMap[creative.id] = creative.l3ChannelId;
    });

    this.channelGroupSetting = campaignInfo.channelGroupId
      ? ChannelGroupSetting.fromId(campaignInfo.channelGroupId)
      : undefined;
  }

  get channel() {
    return this.l1Object.channel;
  }

  get state(): CreativeOverviewStateContentState {
    return {
      selectedCreatives: this.selectedCreatives,
      loading: this.loading,
      dirty: this.dirty
    };
  }

  get bindPermissionAware() {
    const channelAddonFeature = ADDONFEATURE.CHANNEL[this.channel];
    // ECOPD-5566：停用渠道底下的素材轉唯讀
    return (
      channelAddonFeature
        ? hasFuncs(Permission.CAMPAIGN_WRITE)
            .and(addOnEnabled(channelAddonFeature))
            .and(isPmax3Order(this.l1Object.adsOrderId))
            .and(notSelfServeAdObject(this.l1Object))
        : hasFuncs(Permission.CAMPAIGN_WRITE)
            .and(isPmax3Order(this.l1Object.adsOrderId))
            .and(notSelfServeAdObject(this.l1Object))
    ).and(notArchivedChannel(this.l1Object.channelGroupId));
  }

  getEditPath = (creativeId) => {
    const postfitPath = this.campaignInfo.draftId
      ? `&draftId=${this.campaignInfo.draftId}`
      : `&campaignId=${this.campaignInfo.id}`;
    return this.l1Object
      ? `/creatives/${creativeId}/edit?orderNumber=${this.orderNumber}&l1ObjectId=${this.l1Object.l1ObjectId}${postfitPath}`
      : `/creatives/${creativeId}/edit?orderNumber=${this.orderNumber}${postfitPath}`;
  };

  setStateContext = (stateContext) => {
    this.stateContext = stateContext;
  };

  /**
   * 這個活動的銷售渠道是不是「同時只能有一個啟用中的素材」。
   *
   * 電子紙看板的裝置排程一次只播一份素材，綁第二份或多開幾份，投遞端也只會挑一份出來
   * 播，畫面上卻顯示兩份都在跑。判斷方式跟活動表單的固定時段一致，都是看銷售渠道 id。
   */
  get onlyOneActiveCreative(): boolean {
    return isEpaperChannelGroup(this.campaignInfo.channelGroupId);
  }

  /**
   * 看 isActiveBinding 而不是 bindingState === ENABLE。
   *
   * bindingState 是後端算出來的「當下遞送狀態」，素材設成啟用但還沒進 enableStartTime、
   * 或已過 enableEndTime、或還在 IN_PROGRESS，值都不會是 ENABLE。用它判斷會在這些
   * 時候誤以為沒有啟用中的素材而放行第二份。isActiveBinding 才是啟用／停用那顆按鈕
   * 真正在切的旗標，ModifyCreativeActivationStateContentModel 也是用它篩清單。
   */
  get hasActiveCreative(): boolean {
    return this.creatives.some((creative) => creative.isActiveBinding);
  }

  /**
   * 名額滿了（ECOPD-5640）。與 onlyOneActiveCreative 是兩條獨立的限制：那條問「這個渠道
   * 一次只能有一份啟用中素材嗎」，這條問「這檔活動排得進機台嗎」。兩條都要過。
   */
  private get spaceQuotaFull(): boolean {
    return isEpaperSpaceQuotaFull(this.campaignInfo);
  }

  /**
   * 這個活動是否為只能唯讀的 One For All 活動：不能綁定、新增或啟用素材，只能停用。
   * @returns 是 One For All 活動時為 true
   */
  get isReadOnlyCampaign(): boolean {
    return isOneForAllAdType(this.campaignInfo.adType);
  }

  canBind = () => {
    if (this.isReadOnlyCampaign) {
      return false;
    }
    if (this.spaceQuotaFull) {
      return false;
    }
    return !this.onlyOneActiveCreative || !this.hasActiveCreative;
  };

  canActive = () => {
    if (this.isReadOnlyCampaign) {
      return false;
    }
    if (this.spaceQuotaFull) {
      return false;
    }
    if (!this.onlyOneActiveCreative) {
      return true;
    }
    // 一次選好幾個也要擋：全部開起來一樣會有兩份以上啟用中，跟已有啟用中素材是同一件事。
    return !this.hasActiveCreative && this.selectedCreatives.length <= 1;
  };

  getBindBlockedTip = (target: 'bind' | 'create'): string | undefined => {
    if (this.canBind() || this.isReadOnlyCampaign) {
      return undefined;
    }
    if (this.spaceQuotaFull) {
      return i18n.t(
        'creativeOverviewStateContent.buttons.epaperSpaceQuotaFullTips'
      );
    }
    return target === 'bind'
      ? i18n.t('creativeOverviewStateContent.buttons.singleActiveBindTips')
      : i18n.t('creativeOverviewStateContent.buttons.singleActiveCreateTips');
  };

  getActivateBlockedTip = (): string | undefined => {
    if (this.canActive() || this.isReadOnlyCampaign) {
      return undefined;
    }
    if (this.spaceQuotaFull) {
      return i18n.t(
        'creativeOverviewStateContent.buttons.epaperSpaceQuotaFullTips'
      );
    }
    if (this.onlyOneActiveCreative) {
      return i18n.t(
        'creativeOverviewStateContent.buttons.singleActiveActivateTips'
      );
    }
    // 保底：目前 canActive() 只會因為上面兩個理由變 false，所以這一行構不到。留著是為了
    // 以後多一個理由時不會變成空提示。
    return i18n.t('creativeOverviewStateContent.buttons.actionTips');
  };

  canDeactive = () => {
    if (!this.campaignInfo.isPmp) {
      return true;
    }
    const remainedActvieCreatives = this.creatives
      .filter((creative) => !this.selectedCreatives.includes(creative.id))
      .filter(
        (creative) => creative.bindingState === CampaignBannerMapState.ENABLE
      );
    return remainedActvieCreatives.length > 0;
  };

  get listFormatters() {
    return rtbBindingListFormatters;
  }

  get columnsToShow() {
    let basicColumns = [
      CreativeListColumns.ID,
      CreativeListColumns.PREVIEW,
      CreativeListColumns.STATUS,
      CreativeListColumns.DELIVERY,
      CreativeListColumns.RESULTS,
      CreativeListColumns.CTR,
      CreativeListColumns.IMPRES,
      CreativeListColumns.CLICKS,
      CreativeListColumns.UU,
      CreativeListColumns.TYPE,
      CreativeListColumns.APPROVALSTATUS,
      CreativeListColumns.TOOL
    ];
    if (
      (this.campaignInfo.adType &&
        [AdType.SANDBOX_OUTDOOR, AdType.SANDBOX_MOVABLE_OUTDOOR].includes(
          this.campaignInfo.adType as AdType
        )) ||
      (this.channelGroupSetting &&
        this.channelGroupSetting.config.channelType === ChannelType.BEACON)
    ) {
      basicColumns = _.without(basicColumns, CreativeListColumns.PREVIEW);
    }
    return basicColumns;
  }

  async updateStatusMethod(active: boolean) {
    const basicPayload = {
      active,
      draft: this.isDraft
    };
    let payload;
    if (this.isDraft) {
      payload = [
        {
          ...basicPayload,
          goCreativeIds: this.selectedCreatives,
          l2ChannelIds: [this.campaignInfo.draftId]
        }
      ];
    } else {
      payload = [
        {
          ...basicPayload,
          l3ChannelIds: this.selectedCreatives.map(
            (creativeId: number) => this.bindingIdMap[creativeId]
          )
        }
      ];
    }
    await this.bindingManager.updateBinding(
      this.channel,
      this.l1Object.channelGroupId,
      payload
    );
  }

  activeCreatives = async () => {
    if (this.selectedCreatives.length <= 0) {
      return;
    }

    try {
      this.updateState(true);
      await this.updateStatusMethod(true);
      this.creatives = await this.refreshCreative();
      this.selectedCreatives = [];
      toast.success(i18n.t('creativeOverviewState.labels.activeSuccess'));
      this.dirty = true;
      this.updateState();
    } catch (e) {
      toast.error(i18n.t('creativeOverviewState.labels.activeFailed'));
      this.updateState();
    }
  };

  deactiveCreatives = async () => {
    if (this.selectedCreatives.length <= 0) {
      return;
    }

    try {
      this.updateState(true);
      await this.updateStatusMethod(false);
      this.creatives = await this.refreshCreative();
      this.selectedCreatives = [];
      toast.success(i18n.t('creativeOverviewState.labels.deactiveSuccess'));
      this.dirty = true;
      this.updateState();
    } catch (e) {
      toast.error(i18n.t('creativeOverviewState.labels.deactiveFailed'));
      this.updateState();
    }
  };

  handleOnSelect = (creativeId: number) => {
    const creative = this.creatives.find((c) => c.id === creativeId);
    if (creative && isArchivedCampaignCreative(creative)) {
      return;
    }
    if (this.selectedCreatives.indexOf(creativeId) > -1) {
      _.remove(this.selectedCreatives, (id) => id === creativeId);
    } else {
      this.selectedCreatives.push(creativeId);
    }
    this.updateState();
  };

  handleOnSelectAll = () => {
    const selectableIds = this.creatives
      .filter((creative) => !isArchivedCampaignCreative(creative))
      .map((creative) => creative.id);
    if (_.difference(selectableIds, this.selectedCreatives).length === 0) {
      this.selectedCreatives = [];
    } else {
      this.selectedCreatives = selectableIds;
    }

    this.updateState();
  };

  handleOnUnSelectAll = () => {
    this.selectedCreatives = [];
    this.updateState();
  };

  updateState(loading = false) {
    this.loading = loading;
    this.event.fireEvent(this);
  }

  getCreativeListModel(): CreativeListModel {
    return new DefaultCreativeListModel(
      this.l1Object,
      this.campaignInfo,
      this.creatives,
      this.selectedCreatives,
      this.closingReportEnabled,
      this.handleOnSelect,
      this.handleOnSelectAll,
      this.getEditPath,
      this.listFormatters,
      this.columnsToShow,
      this.bindPermissionAware
    );
  }
}
